import { Schema as S, Struct } from "effect";
import { BookingCreateRequestSchema } from "../scheduling/book-appointment.request";
import { TimeSlotList } from "../scheduling/time-slot.schema";
import { BookingAnswerPathSchema } from "./model.schema";
const handle = S.String.pipe(S.check(S.isPattern(/^[A-Za-z0-9_-]{43}$/)));
/** Patient runtime input grants no client-selected destination or revision authority.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingRuntimeRequestSchema)(request);
 */
export const BookingRuntimeRequestSchema = S.Struct({ contextHandle: handle, patientId: S.String.pipe(S.check(S.isMinLength(1))), language: S.String.pipe(S.check(S.isMinLength(1))), path: BookingAnswerPathSchema });
/** Patient runtime projection contains text/answer IDs, never authored routing or metadata.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingRuntimeResponseSchema)({ kind: "booking" });
 */
export const BookingRuntimeResponseSchema = S.Union(
  [S.Struct({ kind: S.Literals(["disabled", "booking"]) }),
  S.Struct({ kind: S.Literal("unavailable"), availableLanguages: S.Array(S.String.pipe(S.check(S.isMinLength(1)))) }),
  S.Struct({ kind: S.Literal("reception"), text: S.String }),
  S.Struct({ kind: S.Literal("question"), questionId: S.String, text: S.String, help: S.optional(S.String), answers: S.Array(S.Struct({ answerId: S.String, text: S.String })) })],
);
/** Typed transient patient runtime response.
 * @since 1.1.8
 * @example const response: BookingRuntimeResponse = { kind: "booking" };
 */
export type BookingRuntimeResponse = S.Schema.Type<typeof BookingRuntimeResponseSchema>;

/** Typed transient runtime request with context and ordered answers.
 * @since 1.1.8
 * @example const request: BookingRuntimeRequest = input;
 */
export type BookingRuntimeRequest = S.Schema.Type<typeof BookingRuntimeRequestSchema>;

/** Context-bound current-route slot query; the server derives destination membership.
 * No caller-provided calendar set, revision or alternative permission is accepted.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingSlotsRequestSchema)(request);
 */
export const BookingSlotsRequestSchema = S.Struct({ ...BookingRuntimeRequestSchema.fields, ...{ appointmentTypeId: S.String.pipe(S.check(S.isMinLength(1))), startDate: S.String.pipe(S.check(S.isMinLength(1))), endDate: S.String.pipe(S.check(S.isMinLength(1))) } });
/** Typed current-route slot query for the questionnaire runtime.
 * @since 1.1.8
 * @example const request: BookingSlotsRequest = input;
 */
export type BookingSlotsRequest = S.Schema.Type<typeof BookingSlotsRequestSchema>;

/** Effective service stage derived only after complete server route evaluation.
 * Calendar restrictions and authored graph metadata stay on the server.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingRouteAvailabilitySchema)({ kind: "reception" });
 */
export const BookingRouteAvailabilitySchema = S.Union([S.Struct({ kind: S.Literal("reception") }), S.Struct({ kind: S.Literals(["primary", "alternatives"]), appointmentTypeIds: S.Array(S.String.pipe(S.check(S.isMinLength(1)))).pipe(S.check(S.isMinLength(1))) })]);
/** Typed patient-safe route availability result.
 * @since 1.1.8
 * @example const result: BookingRouteAvailability = { kind: "reception" };
 */
export type BookingRouteAvailability = S.Schema.Type<typeof BookingRouteAvailabilitySchema>;

/** Receipt-bound submission carries transient IDs, never client-written routing or summaries.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingSubmissionRequestSchema)(request);
 */
export const BookingSubmissionRequestSchema = S.Struct({ ...BookingCreateRequestSchema.fields, ...BookingRuntimeRequestSchema.fields, appointmentTypeId: S.String.pipe(S.check(S.isMinLength(1))), calendarId: S.String.pipe(S.check(S.isMinLength(1))), startTime: S.String.pipe(S.check(S.isMinLength(1))), endTime: S.String.pipe(S.check(S.isMinLength(1))) });
/** Typed context-bound new-create or matching retained-intent submission.
 * @since 1.1.8
 * @example const request: BookingSubmissionRequest = input;
 */
export type BookingSubmissionRequest = S.Schema.Type<typeof BookingSubmissionRequestSchema>;

/** Read-only review input cannot create an intent or supply summary text.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingReviewRequestSchema)(request);
 */
export const BookingReviewRequestSchema = BookingSubmissionRequestSchema.mapFields(Struct.omit(["clientSubmissionId", "recoveryReceipt"]));
/** Typed transient review request.
 * @since 1.1.8
 * @example const request: BookingReviewRequest = input;
 */
export type BookingReviewRequest = S.Schema.Type<typeof BookingReviewRequestSchema>;
/** Patient-safe attributed summary; German payload and semantic fingerprints stay server-side.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingReviewResponseSchema)({ rows: [] });
 */
export const BookingReviewResponseSchema = S.Struct({ rows: S.Array(S.Struct({ questionId: S.String, answerId: S.String, question: S.String, answer: S.String, attribution: S.Literal("patient-response") })), note: S.optional(S.String) });
/** Typed patient review without persistence or create authority.
 * @since 1.1.8
 * @example const response: BookingReviewResponse = { rows: [] };
 */
export type BookingReviewResponse = S.Schema.Type<typeof BookingReviewResponseSchema>;

/** Server-derived route stage accompanies its slots so clients can detect changed offers.
 * Empty displayed months retain the current stage; reception cannot carry bookable slots.
 * @since 1.1.8
 * @example S.decodeUnknownSync(BookingSlotOfferSchema)({ route: { kind: "reception" }, slots: [] });
 */
export const BookingSlotOfferSchema = S.Struct({ route: BookingRouteAvailabilitySchema, slots: TimeSlotList }).pipe(S.check(S.makeFilter((offer) => {
  const route = offer.route;
  return route.kind === "reception" ? offer.slots.length === 0 : offer.slots.every((slot) => route.appointmentTypeIds.includes(slot.appointmentTypeId));
}, { message: "Slots must belong to the server-derived route" })));
/** Current server route and its permitted displayed slots.
 * @since 1.1.8
 * @example const offer: BookingSlotOffer = { route: { kind: "reception" }, slots: [] };
 */
export type BookingSlotOffer = S.Schema.Type<typeof BookingSlotOfferSchema>;
